import React from 'react';
import type { ApolloError } from 'apollo-client';
import Button from '@atlaskit/button';
import EmptyState from '@atlaskit/empty-state';
import ErrorImageUrl from '@atlassian/jira-illustrations/src/ui/adg4/jira/heroes/components/icecream-accident';
import { getAkEmptyStateRenderImageFn } from '@atlassian/jira-illustrations/src/ui/helpers/ak-empty-state/index.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { FireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import messages from './messages';
import type { ReportNames } from './types';
import { getErrorStatus, getErrorType } from './utils';

// turn into enum for all the reports that we have.
export type Props = Intl & {
	chartError: ApolloError | null;
	filtersError: ApolloError | null;
	reportName: ReportNames;
	onClickRetry: () => void;
};

/**
 * An error empty state with a retry button for when there's a fetch failure on this report.
 */
export const ErrorEmptyState = ({
	onClickRetry,
	intl,
	filtersError,
	chartError,
	reportName,
}: Props) => {
	const statusCode = chartError ? getErrorStatus(chartError) : getErrorStatus(filtersError);
	return (
		<>
			<EmptyState
				renderImage={getAkEmptyStateRenderImageFn(ErrorImageUrl)}
				header={intl.formatMessage(messages.fetchError)}
				primaryAction={
					<Button appearance="primary" onClick={() => onClickRetry()}>
						{intl.formatMessage(messages.retryOnError)}
					</Button>
				}
			/>

			<FireOperationalAnalytics
				eventName={`${reportName} failed`}
				attributes={{
					isChartError: !!chartError,
					isFiltersError: !!filtersError,
					errorType: chartError ? getErrorType(chartError) : getErrorType(filtersError),
					statusCode: `${statusCode || 'unknown'}`, // returning as string so we can pass it on to SignalFX as an enum
				}}
			/>
		</>
	);
};

const ErrorEmptyStateWithIntl = injectIntl(ErrorEmptyState);

export default ErrorEmptyStateWithIntl;
